<template>
	<div class="equivalences-container">
		<!-- Enhanced Main Layout with Glass Effect -->
		<div class="glass-container backdrop-blur-lg bg-white/80 rounded-xl shadow-xl border border-white/20 p-4 mb-6">
			<!-- Header Action Buttons (shown when footer buttons are not visible) -->
			<div v-show="showHeaderButtons" class="header-actions-wrapper mb-4">
				<div class="flex flex-col sm:flex-row gap-3 justify-end">
					<vs-button
						class="cancel-btn"
						type="border"
						color="danger"
						icon-pack="feather"
						icon="icon-x"
						size="small"
						@click.prevent="handleCancel"
					>
						{{ $t("Cancel") }}
					</vs-button>

					<vs-button
						v-if="isUploadAddMode"
						class="save-btn"
						color="primary"
						icon-pack="feather"
						:icon="uploadEquivalencesButtonIcon"
						size="small"
						:loading="isProcessing"
						@click="validateUploadEquivalents"
					>
						{{ uploadEquivalencesButtonLabel }}
					</vs-button>

					<vs-button
						v-else
						class="save-btn"
						color="primary"
						icon-pack="feather"
						icon="icon-edit"
						size="small"
						:loading="isProcessing"
						@click.prevent="updateUploadFile()"
					>
						{{ $t("Crud.Update") }}
					</vs-button>
				</div>
			</div>
			<div class="grid grid-cols-1 xl:grid-cols-2 gap-8">
				<!-- Column Mapping Section -->
				<div class="mapping-section">
					<div class="section-header">
						<h3 class="text-xl font-semibold text-gray-800 flex items-center gap-3">
							<div class="icon-wrapper p-2 bg-primary/10 rounded-lg">
								<feather-icon icon="ColumnsIcon" class="w-5 h-5 text-primary" />
							</div>
							{{ $t("Routing.Title.MatchColumn") }}
							<span :title="$t('Routing.Messages.MatchColumnSelectionRoutingP2Tooltip')">
								<feather-icon icon="HelpCircleIcon" class="w-4 h-4 text-gray-400 cursor-help" />
							</span>
						</h3>
						<p class="p-2 text-sm text-gray-600 leading-relaxed">
							{{ $t("Routing.Messages.MatchColumnSelectionRoutingP2") }}
						</p>
					</div>
					<div v-if="validationErrors.length > 0" class="validation-summary mb-4 p-4">
						<div class="flex items-start gap-3">
							<feather-icon icon="AlertTriangleIcon" class="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
							<div>
								<h4 class="text-sm font-medium text-red-700">
									{{ validationErrors.length }} {{ $t("UploadFiles.RequiredFields") }}
								</h4>
								<ul class="mt-1 text-xs text-red-600">
									<li v-for="error in validationErrors" :key="error" class="text-slate-600">
										{{ error }}
									</li>
								</ul>
							</div>
						</div>
					</div>
					<vue-perfect-scrollbar class="mapping-scroll-area" :settings="settings">
						<div class="space-y-4">
							<div
								v-for="key in required_keys"
								:key="key"
								class="field-mapping-card transition-all duration-200 hover:shadow-md"
								:class="getFieldCardClass(key)"
							>
								<!-- Field Header -->
								<div class="field-header flex items-center justify-between mb-2">
									<div class="flex items-center">
										<feather-icon
											:icon="getFieldIcon(key)"
											class="w-5 h-5 text-primary cursor-help hover:text-primary-dark font-bold"
											:class="getFieldIconClass(key)"
										/>
										<div>
											<label
												class="field-label text-lg font-bold text-primary flex items-center gap-2 px-2 py-2 rounded-md border-l-4 border-primary"
											>
												{{ resolvePoliticalDivisionName(key) | capitalize }}
												<span
													v-if="isFieldRequired(key)"
													class="required-indicator text-red-500 font-bold"
													>*</span
												>

												<feather-icon
													v-if="getFieldDescription(key)"
													:title="getFieldDescription(key)"
													icon="InfoIcon"
													class="w-5 h-5 cursor-pointer text-primary cursor-help hover:text-primary-dark transition-colors"
												/>
											</label>
										</div>
									</div>
									<!-- Field Status -->
									<div class="field-status">
										<vs-chip
											v-if="isFieldMapped(key)"
											color="success"
											class="text-emerald-800 text-xs font-semibold"
										>
											<feather-icon icon="CheckCircleIcon" class="w-4 h-4 mr-1" />
											{{ $t("UploadFiles.Mapping") }}
										</vs-chip>
										<vs-chip
											v-else-if="isFieldRequired(key)"
											color="danger"
											class="text-emerald-800 text-xs font-semibold"
										>
											<feather-icon icon="AlertCircleIcon" class="w-4 h-4 mr-1" />
											{{ $t("UploadFiles.Required") }}
										</vs-chip>
										<vs-chip v-else color="warning" class="text-emerald-800 text-xs font-semibold">
											<feather-icon icon="MinusCircleIcon" class="w-4 h-4 mr-1" />
											{{ $t("UploadFiles.Optional") }}
										</vs-chip>
									</div>
								</div>
								<!-- Special Field Handlers -->
								<div v-if="key === 'time_window'">
									<div class="flex items-center gap-2">
										<v-select
											class="w-full md:w-11/12"
											v-model="equivalent_array['time_window']"
											:clearable="true"
											:options="optionTimeWindow"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:multiple="true"
											:disabled="
												equivalent_array.hasOwnProperty('time_window') &&
												equivalent_array['time_window'].length > 0
													? false
													: true
											"
											@input="setSelectedPrompt"
											:placeholder="$t('TimeWindow.SelectTimeWindows')"
										>
											<template v-slot:no-options="{search, searching}">
												<div class="no-options-container p-3 text-center">
													<feather-icon
														icon="ClockIcon"
														class="w-8 h-8 text-gray-300 mx-auto mb-2"
													/>
													<template v-if="searching">
														<p class="text-sm text-gray-500">
															{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
														</p>
													</template>
													<template v-else>
														<p class="text-sm text-gray-500">{{ $t("NoResultsFound") }}</p>
														<vs-button
															class="mt-2"
															size="small"
															icon-pack="feather"
															icon="icon-plus"
															@click="activePrompt = true"
														>
															{{ $t("Add") }} {{ $t("TimeWindow.TimeWindow") }}
														</vs-button>
													</template>
												</div>
											</template>
										</v-select>
										<vs-button
											icon-pack="feather"
											icon="icon-plus"
											type="relief"
											color="primary"
											size="small"
											:title="$t('Add') + ' ' + $t('TimeWindow.TimeWindow')"
											@click="activePrompt = true"
										>
											{{ $t("Add") }}
										</vs-button>
									</div>
								</div>
								<div v-else-if="key === 'breaks' && typeUploadFile === 'VEHICLE_UPLOAD'">
									<vx-input-group>
										<v-select
											class="select-large"
											v-model="equivalent_array['breaks']"
											:clearable="true"
											:options="optionBreaks"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:multiple="true"
											:disabled="
												equivalent_array.hasOwnProperty('breaks') &&
												equivalent_array['breaks'].length > 0
													? false
													: true
											"
											@input="setSelectedPromptBreaks"
										/>
										<template slot="append">
											<div class="append-text btn-addon">
												<vs-button
													icon-pack="feather"
													icon="icon-plus"
													:title="$t('Add')"
													@click="activePromptBreak = true"
												></vs-button>
											</div>
										</template>
									</vx-input-group>
								</div>
								<div v-else-if="key === 'init_depot' && typeUploadFile === 'VEHICLE_UPLOAD'">
									<vx-input-group>
										<v-select
											class="select-large"
											v-model="equivalent_array['init_depot']"
											:clearable="true"
											:options="optionStartDeposit"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											@input="handleInitDepotInput"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
										<template slot="append">
											<div class="append-text btn-addon">
												<vs-button
													icon-pack="feather"
													icon="icon-plus"
													:title="$t('Add')"
													@click="activePromptStartDeposit = true"
												></vs-button>
											</div>
										</template>
									</vx-input-group>
								</div>
								<div v-else-if="key === 'end_depot' && typeUploadFile === 'VEHICLE_UPLOAD'">
									<vx-input-group>
										<v-select
											class="select-large"
											v-model="equivalent_array['end_depot']"
											:clearable="true"
											:options="optionEndDeposit"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											@input="handleEndDepotInput"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
										<template slot="append">
											<div class="append-text btn-addon">
												<vs-button
													icon-pack="feather"
													icon="icon-plus"
													:title="$t('Add')"
													@click="activePromptEndDeposit = true"
												></vs-button>
											</div>
										</template>
									</vx-input-group>
								</div>
								<div v-else-if="key === 'territories' && typeUploadFile === 'VEHICLE_UPLOAD'">
									<vx-input-group>
										<v-select
											class="select-large"
											v-model="equivalent_array['territories']"
											:clearable="true"
											:options="optionTerritories"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:multiple="true"
											:disabled="
												equivalent_array.hasOwnProperty('territories') &&
												equivalent_array['territories'].length > 0
													? false
													: true
											"
											@input="setSelectedPromptTerritories"
										/>
										<template slot="append">
											<div class="append-text btn-addon">
												<vs-button
													icon-pack="feather"
													icon="icon-plus"
													:title="$t('Add')"
													@click="activePromptTerritory = true"
												></vs-button>
											</div>
										</template>
									</vx-input-group>
								</div>
								<div v-else-if="key === 'cost_per_vehicles' && typeUploadFile === 'JOB_UPLOAD'">
									<vx-input-group>
										<v-select
											class="select-large"
											v-model="equivalent_array['cost_per_vehicles']"
											:clearable="true"
											:options="optionCostPerVehicles"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:disabled="
												equivalent_array.hasOwnProperty('cost_per_vehicles') &&
												equivalent_array['cost_per_vehicles'].length > 0
													? false
													: true
											"
											@input="setSelectedPromptCostPerVehicles"
										/>
										<template slot="append">
											<div class="append-text btn-addon">
												<vs-button
													icon-pack="feather"
													icon="icon-plus"
													:title="$t('Add')"
													@click="activePromptCostPerVehicle = true"
												></vs-button>
											</div>
										</template>
									</vx-input-group>
								</div>
								<!-- Standard Field Selector -->
								<div v-else class="standard-field-container">
									<v-select
										class="enhanced-select"
										v-model="equivalent_fields[key]"
										:clearable="true"
										:options="equivalentFieldsFefault"
										:dir="$vs.rtl ? 'rtl' : 'ltr'"
										:value="v_select_selected"
										@input="onFieldChange(key, $event)"
										:multiple="isSelectMultiple(key)"
										:placeholder="getFieldPlaceholder(key)"
										:searchable="true"
									>
										<template v-slot:no-options="{search, searching}">
											<div class="no-options-container p-3 text-center">
												<feather-icon
													icon="SearchIcon"
													class="w-8 h-8 text-gray-300 mx-auto mb-2"
												/>
												<template v-if="searching">
													<p class="text-sm text-gray-500">
														{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
													</p>
												</template>
												<template v-else>
													<p class="text-sm text-gray-500">
														{{ $t("UploadFiles.NoColumnsAvailable") }}
													</p>
												</template>
											</div>
										</template>
									</v-select>
									<div v-if="getSmartSuggestions(key).length > 0" class="smart-suggestions mt-2">
										<p
											class="suggestions-header flex items-center gap-1 text-sm font-medium text-gray-700 mb-1"
										>
											<span>{{ $t("UploadFiles.SmartSuggestions") }}</span>
											<span class="text-xs text-gray-500 font-normal">
												{{ "(" + $t("UploadFiles.ClickToSelect") + ")" }}
											</span>
										</p>
										<div class="flex flex-wrap gap-1">
											<button
												v-for="suggestion in getSmartSuggestions(key)"
												:key="suggestion"
												:class="getSuggestionChipClass(suggestion, key)"
												class="suggestion-chip cursor-pointer px-2 py-1 text-xs rounded-full transition-all duration-300"
												@click="applySuggestion(key, suggestion)"
												:disabled="equivalent_fields[key] === suggestion"
											>
												{{ suggestion }}
											</button>
										</div>
									</div>
								</div>
							</div>
						</div>
					</vue-perfect-scrollbar>
				</div>
				<!-- Enhanced Data Preview Section -->
				<div class="preview-section">
					<div class="section-header">
						<h3 class="text-xl font-semibold text-gray-800 flex items-center gap-3">
							<div class="icon-wrapper p-2 bg-green-100 rounded-lg">
								<feather-icon icon="EyeIcon" class="w-5 h-5 text-green-600" />
							</div>
							{{ $t("Routing.Title.TableExample") }}
							<div class="preview-stats flex items-center gap-4 ml-auto">
								<div class="stat-item">
									<span class="text-xs text-gray-500">{{ $t("UploadFiles.Records") }}:</span>
									<span class="text-sm font-semibold text-gray-700">{{ uploadRecords.length }}</span>
								</div>
								<div class="stat-item">
									<span class="text-xs text-gray-500">{{ $t("UploadFiles.Columns") }}:</span>
									<span class="text-sm font-semibold text-gray-700">{{ headers.length }}</span>
								</div>
							</div>
						</h3>
						<p class="p-2 text-sm text-gray-600 leading-relaxed">
							{{ $t("Routing.Title.TableExampleText") }}
						</p>
					</div>

					<!-- Enhanced Data Table -->
					<div class="data-preview-container">
						<vue-perfect-scrollbar class="enhanced-table-scroll" :settings="settings">
							<div class="overflow-x-auto">
								<table class="enhanced-data-table w-full">
									<caption></caption>
									<thead>
										<tr class="table-header">
											<th
												v-for="header in headers"
												:key="header"
												class="header-cell"
												:class="getHeaderClass(header)"
											>
												<div class="header-content">
													<span class="header-text">{{ header | mayus }}</span>
													<div v-if="isHeaderMapped(header)" class="mapping-indicator">
														<feather-icon icon="LinkIcon" class="w-3 h-3 text-green-500" />
													</div>
												</div>
											</th>
										</tr>
									</thead>
									<tbody>
										<tr
											v-for="(record, index) in previewRecords"
											:key="`record-${index}`"
											class="table-row"
										>
											<td
												v-for="header in headers"
												:key="`cell-${index}-${header}`"
												class="table-cell"
												:class="getCellClass(header, record[header])"
											>
												<div class="cell-content">
													<span class="cell-text">{{ formatCellValue(record[header]) }}</span>
													<div v-if="isCellEmpty(record[header])" class="empty-indicator">
														<feather-icon
															icon="AlertCircleIcon"
															class="w-3 h-3 text-amber-400"
														/>
													</div>
												</div>
											</td>
										</tr>
										<!-- Skeleton Placeholder Rows -->
										<template v-if="placeholderRowCount > 0">
											<tr
												v-for="n in placeholderRowCount"
												:key="`placeholder-row-${n}`"
												class="table-row is-placeholder"
											>
												<td
													v-for="header in headers"
													:key="`placeholder-cell-${n}-${header}`"
													class="table-cell"
												>
													<div class="skeleton-placeholder"></div>
												</td>
											</tr>
										</template>
									</tbody>
								</table>
							</div>
						</vue-perfect-scrollbar>
					</div>
				</div>
			</div>
		</div>

		<!-- Sticky Action Bar -->
		<div class="sticky-actions-wrapper" ref="stickyActions">
			<div class="flex flex-col sm:flex-row gap-3 justify-end">
				<vs-button
					class="cancel-btn"
					type="border"
					color="danger"
					icon-pack="feather"
					icon="icon-x"
					size="small"
					@click.prevent="handleCancel"
				>
					{{ $t("Cancel") }}
				</vs-button>

				<vs-button
					v-if="isUploadAddMode"
					class="save-btn"
					color="primary"
					icon-pack="feather"
					:icon="uploadEquivalencesButtonIcon"
					size="small"
					:loading="isProcessing"
					@click="validateUploadEquivalents"
				>
					{{ uploadEquivalencesButtonLabel }}
				</vs-button>

				<vs-button
					v-else
					class="save-btn"
					color="primary"
					icon-pack="feather"
					icon="icon-edit"
					size="small"
					:loading="isProcessing"
					@click.prevent="updateUploadFile()"
				>
					{{ $t("Crud.Update") }}
				</vs-button>
			</div>
		</div>

		<!-- Popup Alerts -->
		<vs-popup style="z-index: 54000" :title="popupTitle" :active.sync="popupActiveAlert">
			<alert size="small" :title="alert_title" :message="alert_message" :color="alert_color"></alert>
		</vs-popup>

		<!-- Validation Errors Popup -->
		<vs-popup :title="$t('Routing.Title.MatchColumn')" :active.sync="equivalentDataErrorsAlert">
			<vs-alert icon="warning" :active="equivalentDataErrorsAlert" color="danger" class="my-3">
				<span>{{ $t("ManualData.ErrorsFound") }}.</span>
			</vs-alert>
			<vue-perfect-scrollbar class="scroll-area-table" :settings="settings">
				<vs-table :data="equivalentDataErrors">
					<template slot="thead">
						<vs-th>{{ $t("Register") }} N</vs-th>
						<vs-th>{{ $t("Error") }}</vs-th>
					</template>
					<template slot-scope="{data}">
						<vs-tr :key="indextr" v-for="(tr, indextr) in data">
							<vs-td :data="data[indextr].index">
								{{ data[indextr].index + 1 }}
							</vs-td>
							<vs-td :data="data[indextr].index">
								<span>{{ $t("ManualData.PropertyError") }} </span>
								<span class="font-semibold"
									>{{ identifyPropertyName(data[indextr].message) | title }},
								</span>
								<span> {{ identifyMessageError(data[indextr].message) }}</span>
							</vs-td>
						</vs-tr>
					</template>
				</vs-table>
			</vue-perfect-scrollbar>
		</vs-popup>

		<!-- Time Window Prompt -->
		<vs-prompt
			class="prompt-content"
			:title="$t('TimeWindow.TimeWindows')"
			:active.sync="activePrompt"
			:acceptText="$t('Accept')"
			:cancelText="$t('Cancel')"
			@accept="acceptPrompt"
		>
			<div v-if="typeUploadFile === 'VISIT_POINT_UPLOAD'" class="flex items-center justify-end">
				<vs-button
					class="font-bold mb-2"
					type="line"
					icon-pack="feather"
					icon="icon-plus"
					@click.prevent="addTimeWindow()"
					>{{ $t("Add") }}
				</vs-button>
			</div>
			<vue-perfect-scrollbar class="scroll-area-table" :settings="settings">
				<transition name="slide-fade">
					<form>
						<vx-card
							v-for="(item, index) in time_window"
							collapse-action
							class="mb-2"
							:title="$t('TimeWindow.TimeWindow') + ' # ' + (index + 1)"
							:key="index"
							:cardBorder="true"
							:noShadow="true"
						>
							<feather-icon
								v-if="typeUploadFile === 'VISIT_POINT_UPLOAD' && time_window.length > 1 && index != 0"
								icon="Trash2Icon"
								class="flex items-center justify-end"
								svgClasses="h-5 w-5 hover:text-warning text-danger mr-6 cursor-pointer"
								:title="$t('Crud.Delete') + ' ' + $t('TimeWindow.TimeWindow')"
								@click.prevent="removeTimeWindow(index)"
							/>
							<vs-alert
								:active="validateTimeWindow && time_window.length - 1 === index"
								class="mt-2"
								color="danger"
								icon="new_releases"
							>
								{{ $t("TimeWindow.AllDataRequestedAddedTimewindowAreRequired") }}
							</vs-alert>
							<form>
								<div class="vx-row">
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("CompanyCreate.InitDate") }}</small>
										<v-select
											class="select-large"
											v-model="item['init_date_window']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPrompt"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("CompanyCreate.EndDate") }}</small>
										<v-select
											class="select-large"
											v-model="item['end_date_window']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPrompt"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("Routing.Table.InitHour") }}</small>
										<v-select
											class="select-large"
											v-model="item['init_hour_window']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPrompt"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("Routing.Table.EndHour") }}</small>
										<v-select
											class="select-large"
											v-model="item['end_hour_window']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPrompt"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
								</div>
							</form>
							<vs-alert
								v-if="typeUploadFile === 'VISIT_POINT_UPLOAD' && time_window.length > 1 && index === 0"
								active
								class="mt-2"
								color="primary"
							>
								<span>
									{{ $t("TimeWindow.RemoveAllOtherTimeWindowsToEnable") }}
								</span>
							</vs-alert>
							<vx-card
								v-else-if="index === 0"
								class="mt-2"
								:title="$t('TimeWindow.SoftTimeWindow')"
								:subtitle="$t('TimeWindow.ThisWillBeEliminatedSecondTimeWindow')"
								:cardBorder="true"
								:noShadow="true"
							>
								<form>
									<vs-alert
										:active.sync="softWindowAlert"
										closable
										class="mb-2"
										color="primary"
										icon="info"
									>
										{{ $t("TimeWindow.DateAndTimeMustBeWithinTheGeneral") }}
									</vs-alert>
									<div class="vx-row">
										<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
											<small class="date-label">{{ $t("CompanyCreate.InitDate") }}</small>
											<v-select
												class="select-large"
												v-model="item.soft_time_window['init_date']"
												:clearable="true"
												:options="equivalentFieldsFefault"
												:dir="$vs.rtl ? 'rtl' : 'ltr'"
												:value="v_select_selected"
												:placeholder="$t('Select')"
												@input="setSelectedPromptSoftTimeWindow"
											>
												<template v-slot:no-options="{search, searching}">
													<template v-if="searching">
														{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
													</template>
													<template v-else>
														{{ $t("NoResultsFound") }}
													</template>
												</template>
											</v-select>
										</div>
										<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
											<small class="date-label">{{ $t("CompanyCreate.EndDate") }}</small>
											<v-select
												class="select-large"
												v-model="item.soft_time_window['end_date']"
												:clearable="true"
												:options="equivalentFieldsFefault"
												:dir="$vs.rtl ? 'rtl' : 'ltr'"
												:value="v_select_selected"
												:placeholder="$t('Select')"
												@input="setSelectedPromptSoftTimeWindow"
											>
												<template v-slot:no-options="{search, searching}">
													<template v-if="searching">
														{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
													</template>
													<template v-else>
														{{ $t("NoResultsFound") }}
													</template>
												</template>
											</v-select>
										</div>
										<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
											<small class="date-label">{{ $t("Routing.Table.InitHour") }}</small>
											<v-select
												class="select-large"
												v-model="item.soft_time_window['init_hour']"
												:clearable="true"
												:options="equivalentFieldsFefault"
												:dir="$vs.rtl ? 'rtl' : 'ltr'"
												:value="v_select_selected"
												:placeholder="$t('Select')"
												@input="setSelectedPromptSoftTimeWindow"
											>
												<template v-slot:no-options="{search, searching}">
													<template v-if="searching">
														{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
													</template>
													<template v-else>
														{{ $t("NoResultsFound") }}
													</template>
												</template>
											</v-select>
										</div>
										<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
											<small class="date-label">{{ $t("Routing.Table.EndHour") }}</small>
											<v-select
												class="select-large"
												v-model="item.soft_time_window['end_hour']"
												:clearable="true"
												:options="equivalentFieldsFefault"
												:dir="$vs.rtl ? 'rtl' : 'ltr'"
												:value="v_select_selected"
												:placeholder="$t('Select')"
												@input="setSelectedPromptSoftTimeWindow"
											>
												<template v-slot:no-options="{search, searching}">
													<template v-if="searching">
														{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
													</template>
													<template v-else>
														{{ $t("NoResultsFound") }}
													</template>
												</template>
											</v-select>
										</div>
										<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
											<small class="date-label">{{
												$t("TimeWindow.PenaltyForAnticipation")
											}}</small>
											<v-select
												class="select-large"
												v-model="item.soft_time_window['cost_per_hour_before']"
												:clearable="true"
												:options="equivalentFieldsFefault"
												:dir="$vs.rtl ? 'rtl' : 'ltr'"
												:value="v_select_selected"
												:placeholder="$t('Select')"
												@input="setSelectedPromptSoftTimeWindow"
											>
												<template v-slot:no-options="{search, searching}">
													<template v-if="searching">
														{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
													</template>
													<template v-else>
														{{ $t("NoResultsFound") }}
													</template>
												</template>
											</v-select>
										</div>
										<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
											<small class="date-label">{{ $t("TimeWindow.PenaltyForLateness") }}</small>
											<v-select
												class="select-large"
												v-model="item.soft_time_window['cost_per_hour_after']"
												:clearable="true"
												:options="equivalentFieldsFefault"
												:dir="$vs.rtl ? 'rtl' : 'ltr'"
												:value="v_select_selected"
												:placeholder="$t('Select')"
												@input="setSelectedPromptSoftTimeWindow"
											>
												<template v-slot:no-options="{search, searching}">
													<template v-if="searching">
														{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
													</template>
													<template v-else>
														{{ $t("NoResultsFound") }}
													</template>
												</template>
											</v-select>
										</div>
									</div>
								</form>
							</vx-card>
						</vx-card>
					</form>
				</transition>
			</vue-perfect-scrollbar>
		</vs-prompt>
		<vs-prompt
			class="prompt-content"
			:title="$t('Breaks')"
			:active.sync="activePromptBreak"
			:acceptText="$t('Accept')"
			:cancelText="$t('Cancel')"
			@accept="acceptPromptBreaks"
		>
			<div class="flex items-center justify-end">
				<vs-button
					class="font-bold mb-2"
					type="line"
					icon-pack="feather"
					icon="icon-plus"
					@click.prevent="addBreak()"
					>{{ $t("Add") }}
				</vs-button>
			</div>
			<vue-perfect-scrollbar class="scroll-area-table" :settings="settings">
				<vs-alert :active="validateBreak" class="mb-2" color="danger" icon="new_releases">
					{{ $t("TimeWindow.AllDataRequestedAddedTimewindowAreRequired") }}
				</vs-alert>
				<transition name="slide-fade">
					<form>
						<vx-card
							v-for="(item, index) in breaks"
							collapse-action
							class="mb-2"
							:title="$t('Breaks') + ' # ' + (index + 1)"
							:key="index"
							:cardBorder="true"
							:noShadow="true"
						>
							<feather-icon
								v-if="breaks.length > 1"
								icon="Trash2Icon"
								class="flex items-center justify-end"
								svgClasses="h-5 w-5 hover:text-warning text-danger mr-6 cursor-pointer"
								:title="$t('Crud.Delete') + ' ' + $t('Breaks')"
								@click.prevent="removeBreak(index)"
							/>
							<form>
								<div class="vx-row">
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("CompanyCreate.InitDate") }}</small>
										<v-select
											class="select-large"
											v-model="item['init_date_window']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPromptBreaks"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("CompanyCreate.EndDate") }}</small>
										<v-select
											class="select-large"
											v-model="item['end_date_window']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPromptBreaks"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("Routing.Table.InitHour") }}</small>
										<v-select
											class="select-large"
											v-model="item['init_hour_window']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPromptBreaks"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("Routing.Table.EndHour") }}</small>
										<v-select
											class="select-large"
											v-model="item['end_hour_window']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPromptBreaks"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
								</div>
							</form>
						</vx-card>
					</form>
				</transition>
			</vue-perfect-scrollbar>
		</vs-prompt>
		<vs-prompt
			:title="$t('StartDeposit')"
			:active.sync="activePromptStartDeposit"
			:acceptText="$t('Accept')"
			:cancelText="$t('Cancel')"
			@accept="acceptPromptStartDeposit"
		>
			<form>
				<div class="vx-row">
					<div class="vx-col w-full">
						<small class="date-label">{{ $t("Routing.Map.Latitude") }}</small>
						<v-select
							class="select-large"
							v-model="init_depot['latitude']"
							:clearable="true"
							:options="equivalentFieldsFefault"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
							:value="v_select_selected"
							:placeholder="$t('Select')"
							@input="setSelectedPromptStartDepot"
						>
							<template v-slot:no-options="{search, searching}">
								<template v-if="searching">
									{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
								</template>
								<template v-else>
									{{ $t("NoResultsFound") }}
								</template>
							</template>
						</v-select>
					</div>
				</div>
				<div class="vx-row">
					<div class="vx-col w-full">
						<small class="date-label">{{ $t("Routing.Map.Longitude") }}</small>
						<v-select
							class="select-large"
							v-model="init_depot['longitude']"
							:clearable="true"
							:options="equivalentFieldsFefault"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
							:value="v_select_selected"
							:placeholder="$t('Select')"
							@input="setSelectedPromptStartDepot"
						>
							<template v-slot:no-options="{search, searching}">
								<template v-if="searching">
									{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
								</template>
								<template v-else>
									{{ $t("NoResultsFound") }}
								</template>
							</template>
						</v-select>
					</div>
				</div>
			</form>
		</vs-prompt>
		<vs-prompt
			:title="$t('EndDeposit')"
			:active.sync="activePromptEndDeposit"
			:acceptText="$t('Accept')"
			:cancelText="$t('Cancel')"
			@accept="acceptPromptEndDeposit"
		>
			<form>
				<div class="vx-row">
					<div class="vx-col w-full">
						<small class="date-label">{{ $t("Routing.Map.Latitude") }}</small>
						<v-select
							class="select-large"
							v-model="end_depot['latitude']"
							:clearable="true"
							:options="equivalentFieldsFefault"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
							:value="v_select_selected"
							:placeholder="$t('Select')"
							@input="setSelectedPromptEndDepot"
						>
							<template v-slot:no-options="{search, searching}">
								<template v-if="searching">
									{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
								</template>
								<template v-else>
									{{ $t("NoResultsFound") }}
								</template>
							</template>
						</v-select>
					</div>
				</div>
				<div class="vx-row">
					<div class="vx-col w-full">
						<small class="date-label">{{ $t("Routing.Map.Longitude") }}</small>
						<v-select
							class="select-large"
							v-model="end_depot['longitude']"
							:clearable="true"
							:options="equivalentFieldsFefault"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
							:value="v_select_selected"
							:placeholder="$t('Select')"
							@input="setSelectedPromptEndDepot"
						>
							<template v-slot:no-options="{search, searching}">
								<template v-if="searching">
									{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
								</template>
								<template v-else>
									{{ $t("NoResultsFound") }}
								</template>
							</template>
						</v-select>
					</div>
				</div>
			</form>
		</vs-prompt>
		<vs-prompt
			class="prompt-content"
			:title="$t('Territories.Territories')"
			:active.sync="activePromptTerritory"
			:acceptText="$t('Accept')"
			:cancelText="$t('Cancel')"
			@accept="acceptPromptTerritories"
		>
			<div class="flex items-center justify-end">
				<vs-button
					class="font-bold mb-2"
					type="line"
					icon-pack="feather"
					icon="icon-plus"
					@click.prevent="addTerritory()"
					>{{ $t("Add") }}
				</vs-button>
			</div>
			<vue-perfect-scrollbar class="scroll-area-table" :settings="settings">
				<vs-alert :active="validateTerritory" class="mb-2" color="danger" icon="new_releases">
					{{ $t("TimeWindow.AllDataRequestedAddedTimewindowAreRequired") }}
				</vs-alert>
				<transition name="slide-fade">
					<form>
						<vx-card
							v-for="(item, index) in territories"
							collapse-action
							class="mb-2"
							:title="$t('Territories.Territories') + ' # ' + (index + 1)"
							:key="index"
							:cardBorder="true"
							:noShadow="true"
						>
							<feather-icon
								v-if="territories.length > 1"
								icon="Trash2Icon"
								class="flex items-center justify-end"
								svgClasses="h-5 w-5 hover:text-warning text-danger mr-6 cursor-pointer"
								:title="$t('Crud.Delete') + ' ' + $t('Territories.Territories')"
								@click.prevent="removeTerritory(index)"
							/>
							<form>
								<div class="vx-row">
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("Territories.TerritoryID") }}</small>
										<v-select
											class="select-large"
											v-model="item['id']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPromptTerritories"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
									<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
										<small class="date-label">{{ $t("Priority") }}</small>
										<v-select
											class="select-large"
											v-model="item['priority']"
											:clearable="true"
											:options="equivalentFieldsFefault"
											:dir="$vs.rtl ? 'rtl' : 'ltr'"
											:value="v_select_selected"
											:placeholder="$t('Select')"
											@input="setSelectedPromptTerritories"
										>
											<template v-slot:no-options="{search, searching}">
												<template v-if="searching">
													{{ $t("NoResultsFoundFor") + " " }}<em>{{ search }}</em>
												</template>
												<template v-else>
													{{ $t("NoResultsFound") }}
												</template>
											</template>
										</v-select>
									</div>
								</div>
							</form>
						</vx-card>
					</form>
				</transition>
			</vue-perfect-scrollbar>
		</vs-prompt>
		<vs-prompt
			class="prompt-content"
			:title="$t('Jobs.CostPerVehicle')"
			:active.sync="activePromptCostPerVehicle"
			:acceptText="$t('Accept')"
			:cancelText="$t('Cancel')"
			@accept="acceptPromptCostPerVehicles"
		>
			<vs-alert :active="validateCostPerVehicle" class="mb-2" color="danger" icon="new_releases">
				{{ $t("Jobs.CostPerVehicleColumnsRequired") }}
			</vs-alert>
			<form>
				<div class="vx-row">
					<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
						<small class="date-label">{{ $t("ResourceModule.Table.Identification") }}</small>
						<v-select
							class="select-large"
							v-model="costPerVehicles[0]['identification']"
							:clearable="true"
							:options="equivalentFieldsFefault"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
							:placeholder="$t('Select')"
							@input="setSelectedPromptCostPerVehicles"
						/>
					</div>
					<div class="vx-col md:w-1/2 sm:w-1/2 w-full">
						<small class="date-label">{{ $t("Cost") }}</small>
						<v-select
							class="select-large"
							v-model="costPerVehicles[0]['cost']"
							:clearable="true"
							:options="equivalentFieldsFefault"
							:dir="$vs.rtl ? 'rtl' : 'ltr'"
							:placeholder="$t('Select')"
							@input="setSelectedPromptCostPerVehicles"
						/>
					</div>
				</div>
			</form>
		</vs-prompt>
	</div>
</template>

<script>
import vSelect from "vue-select";
import VuePerfectScrollbar from "vue-perfect-scrollbar";
import Commons from "@/helpers/commons.js";
import Alert from "@/components/alerts/Alert.vue";
import moduleOptimization from "@/store/optimization/moduleOptimization.js";

export default {
	components: {
		vSelect,
		Alert,
		VuePerfectScrollbar,
	},
	props: {
		customConfig: {
			type: Object,
			default: () => {
				return {
					actionDropdown: true,
					btnAssigGroup: false,
					columns: ["name", "description", "address", "hour_init", "hour_end", "action"],
				};
			},
		},
		typeUploadFile: {
			type: String,
			default: "VISIT_POINT_UPLOAD",
		},
		activateListUploadFile: {
			type: Boolean,
			default: true,
		},
		typeServiceVisitPoint: {
			type: String,
			default: "PICKUP",
		},
	},
	data() {
		return {
			selected: [],
			itemsPerPage: 5,
			isMounted: false,
			addNewDataSidebar: false,
			activePrompt: false,
			validateTimeWindow: false,
			activePromptBreak: false,
			validateBreak: false,
			optionBreaks: [],
			sidebarData: {},
			currentPageLocal: 1,
			quantityElementPage: 10,
			equivalent_fields_default_selected: [],
			equivalent_fields: {},
			equivalent_array: [],
			v_select_selected: "add",
			popupActiveAlert: false,
			alert_title: "",
			alert_message: "",
			alert_color: "",
			popupTitle: "",
			equivalentDataErrors: [],
			equivalentDataErrorsAlert: false,
			optionTimeWindow: [],
			optionStartDeposit: [],
			optionEndDeposit: [],
			equivalentFields: {},
			equivalentFieldsResult: {},
			activePromptStartDeposit: false,
			activePromptEndDeposit: false,
			softWindowAlert: true,
			activePromptTerritory: false,
			validateTerritory: false,
			optionTerritories: [],

			// Enhanced UX/UI properties
			isProcessing: false,
			validationErrors: [],
			showHeaderButtons: false,
			intersectionObserver: null,
			dropdownObserver: null,
			fieldDescriptions: {
				identification: this.$t("FieldDescriptions.identification"),
				longitude: this.$t("FieldDescriptions.longitude"),
				latitude: this.$t("FieldDescriptions.latitude"),
				priority: this.$t("FieldDescriptions.priority"),
				time_service: this.$t("FieldDescriptions.time_service"),
				skills: this.$t("FieldDescriptions.skills"),
				demand: this.$t("FieldDescriptions.demand"),
				allowed_vehicles: this.$t("FieldDescriptions.allowed_vehicles"),
				time_window: this.$t("FieldDescriptions.time_window"),
				additional_info: this.$t("FieldDescriptions.additional_info"),
				territory_id: this.$t("FieldDescriptions.territory_id"),
				profile: this.$t("FieldDescriptions.profile"),
				max_travel_distance: this.$t("FieldDescriptions.max_travel_distance"),
				max_travel_duration: this.$t("FieldDescriptions.max_travel_duration"),
				init_depot: this.$t("FieldDescriptions.init_depot"),
				end_depot: this.$t("FieldDescriptions.end_depot"),
				capacity: this.$t("FieldDescriptions.capacity"),
				breaks: this.$t("FieldDescriptions.breaks"),
				time_cost: this.$t("FieldDescriptions.time_cost"),
				traveled_hour_cost: this.$t("FieldDescriptions.traveled_hour_cost"),
				distance_cost: this.$t("FieldDescriptions.distance_cost"),
				fixed_cost: this.$t("FieldDescriptions.fixed_cost"),
				strict_territories: this.$t("FieldDescriptions.strict_territories"),
				territories: this.$t("FieldDescriptions.territories"),
				original_identification: this.$t("FieldDescriptions.original_identification"),
			},
			fieldHints: {
				identification: this.$t("FieldHints.identification"),
				longitude: this.$t("FieldHints.longitude"),
				latitude: this.$t("FieldHints.latitude"),
				priority: this.$t("FieldHints.priority"),
				time_service: this.$t("FieldHints.time_service"),
				skills: this.$t("FieldHints.skills"),
				demand: this.$t("FieldHints.demand"),
				time_window: this.$t("FieldHints.time_window"),
			},
			json_base_visit_point: {
				identification: "",
				original_identification: "",
				longitude: "",
				latitude: "",
				priority: "",
				time_service: "",
				skills: [],
				demand: [],
				allowed_vehicles: [],
				time_window: [],
				additional_info: [],
				territory_id: "",
			},
			json_base_vehicle: {
				identification: "",
				original_identification: "",
				profile: "",
				max_travel_distance: "",
				max_travel_duration: "",
				init_depot: {},
				end_depot: {},
				skills: [],
				capacity: [],
				breaks: [],
				time_cost: "",
				traveled_hour_cost: "",
				distance_cost: "",
				fixed_cost: "",
				additional_info: [],
				strict_territories: "",
				territories: [],
				time_window: {},
			},
			json_base_job: {
				name: "",
				pickups: [],
				deliveries: [],
				cost_per_vehicles: [],
				ignore: "",
				penalty_cost: "",
				pickup_to_delivery_absolute_detour_limit: "",
				pickup_to_delivery_time_limit: "",
				pickup_to_delivery_relative_detour_limit: "",
			},
			activePromptCostPerVehicle: false,
			validateCostPerVehicle: false,
			optionCostPerVehicles: [],
			costPerVehicles: [
				{
					identification: "",
					cost: "",
				},
			],
			pagination: {
				next_page: 0,
				total_pages: 0,
				total_records: 0,
			},
			settings: {
				maxScrollbarLength: 60,
				wheelSpeed: 0.6,
			},
			time_window: [
				{
					init_date_window: "",
					end_date_window: "",
					init_hour_window: "",
					end_hour_window: "",
					soft_time_window: {
						init_date: "",
						end_date: "",
						init_hour: "",
						end_hour: "",
						cost_per_hour_before: "",
						cost_per_hour_after: "",
					},
				},
			],
			breaks: [
				{
					init_date_window: "",
					end_date_window: "",
					init_hour_window: "",
					end_hour_window: "",
				},
			],
			territories: [
				{
					id: "",
					priority: "",
				},
			],
			init_depot: {
				longitude: "",
				latitude: "",
			},
			end_depot: {
				longitude: "",
				latitude: "",
			},
		};
	},
	computed: {
		currentPage: {
			get() {
				return this.currentPageLocal;
			},
			set(val) {
				this.currentPageLocal = val;
			},
		},
		totalPages() {
			if (this.pagination.total_pages > 0) return this.pagination.total_pages;
			else return 0;
		},
		uploadRecords() {
			return this.$store.state.uploadFile.uploadedInfoTemporal.records;
		},
		headers() {
			return this.$store.state.uploadFile.uploadedInfoTemporal.keys;
		},
		required_keys() {
			const json_base = this.getJsonBaseForUpload();
			return Object.keys(json_base).map((el) => {
				return el;
			});
		},
		resolvePoliticalDivisionName() {
			return (el) => {
				if (el === "department") return "dpto / state / prov / region";
				if (el === "city") return "city / mun / comu / cton / dsto";
				return el;
			};
		},
		isSelectMultiple() {
			return function (key) {
				const json_base = this.getJsonBaseForUpload();
				return Array.isArray(json_base[key]);
			};
		},
		equivalent_options() {
			return this.$store.state.uploadFile.uploadedInfoTemporal.keys.map((el) => {
				return el;
			});
		},
		queriedItems() {
			return this.$refs.table ? this.$refs.table.queriedResults.length : this.uploadRecords.length;
		},
		equivalentFieldsFefault: {
			get() {
				return this.equivalent_options.filter(
					(e) => !this.equivalent_fields_default_selected.some((eq) => eq === e)
				);
			},
			set(val) {},
		},
		identifyPropertyName() {
			return function (message) {
				return message.split(" ")[0];
			};
		},
		identifyMessageError() {
			return function (message) {
				return this.findText("has an invalid value", message) ? this.$t("ManualData.InvalidValue") : message;
			};
		},
		validateJsonBase() {
			let json_base = {};
			if (this.typeUploadFile === "VISIT_POINT_UPLOAD") {
				json_base = {
					identification: "",
					longitude: "",
					latitude: "",
				};
			} else if (this.typeUploadFile === "VEHICLE_UPLOAD") {
				json_base = {
					identification: "",
				};
			} else if (this.typeUploadFile === "JOB_UPLOAD") {
				json_base = {
					name: "",
					pickups: [],
					deliveries: [],
				};
			}
			return json_base;
		},
		isUploadAddMode() {
			return this.activateListUploadFile || this.typeUploadFile === "JOB_UPLOAD";
		},
		uploadEquivalencesButtonLabel() {
			return this.typeUploadFile === "JOB_UPLOAD" ? this.$t("Add") : this.$t("Save");
		},
		uploadEquivalencesButtonIcon() {
			return this.typeUploadFile === "JOB_UPLOAD" ? "icon-plus" : "icon-save";
		},
		validFormPromptStartDepot() {
			return (
				Object.entries(this.init_depot).length > 0 &&
				this.init_depot.latitude != null &&
				this.init_depot.longitude != null
			);
		},
		standardInput() {
			return this.$store.state.moduleOptimization.standardInputList;
		},
		previewRecords() {
			return this.uploadRecords;
		},

		mappedFieldsCount() {
			return Object.values(this.equivalent_fields).filter(
				(value) => value !== null && value !== "" && value !== undefined
			).length;
		},

		totalRequiredFields() {
			return this.required_keys.length;
		},

		mappingCompletionPercentage() {
			return this.totalRequiredFields > 0
				? Math.round((this.mappedFieldsCount / this.totalRequiredFields) * 100)
				: 0;
		},
		placeholderRowCount() {
			const minRows = 13;
			if (!this.uploadRecords || this.uploadRecords.length >= minRows) {
				return 0;
			}
			return minRows - this.uploadRecords.length;
		},
	},
	methods: {
		getJsonBaseForUpload() {
			if (this.typeUploadFile === "VISIT_POINT_UPLOAD") {
				return this.json_base_visit_point;
			}
			if (this.typeUploadFile === "JOB_UPLOAD") {
				return this.json_base_job;
			}
			return this.json_base_vehicle;
		},
		setupIntersectionObserver() {
			// Create intersection observer to watch the sticky actions
			this.intersectionObserver = new IntersectionObserver(
				(entries) => {
					entries.forEach((entry) => {
						// Show header buttons when footer buttons are not fully visible
						this.showHeaderButtons = !entry.isIntersecting;
					});
				},
				{
					threshold: 0.1, // Trigger when 10% of the element is visible
					rootMargin: "0px 0px -50px 0px", // Add some margin to trigger earlier
				}
			);

			// Start observing the sticky actions element
			if (this.$refs.stickyActions) {
				this.intersectionObserver.observe(this.$refs.stickyActions);
			}
		},
		setSelected() {
			this.setEquivalents();
		},
		setSelectedPrompt() {
			if (this.typeUploadFile === "VEHICLE_UPLOAD") {
				const item = this.time_window[0];
				this.equivalentFields["init_date_window"] = item.init_date_window;
				this.equivalentFields["end_date_window"] = item.end_date_window;
				this.equivalentFields["init_hour_window"] = item.init_hour_window;
				this.equivalentFields["end_hour_window"] = item.end_hour_window;
				this.equivalent_fields_default_selected = Object.values(this.equivalentFields);
				this.equivalentFieldsResult["time_window"] = this.time_window[0];
			} else {
				this.time_window.find((item, index) => {
					const [init_date_window, end_date_window, init_hour_window, end_hour_window] = Object.keys(item);
					const [init_date, end_date, init_hour, end_hour] = Object.values(item);
					const valueIndex = index + 1;
					this.equivalentFields[init_date_window + valueIndex] = init_date;
					this.equivalentFields[end_date_window + valueIndex] = end_date;
					this.equivalentFields[init_hour_window + valueIndex] = init_hour;
					this.equivalentFields[end_hour_window + valueIndex] = end_hour;
				});
				this.equivalent_fields_default_selected = Object.values(this.equivalentFields);
				this.equivalentFieldsResult["time_window"] = this.time_window;
			}
		},
		setSelectedPromptSoftTimeWindow() {
			if (this.typeUploadFile === "VEHICLE_UPLOAD") {
				const item = this.time_window[0];
				if (item.soft_time_window) {
					const [init_date, end_date, init_hour, end_hour, cost_per_hour_before, cost_per_hour_after] =
						Object.keys(item.soft_time_window);
					const [
						init_date_soft,
						end_date_soft,
						init_hour_soft,
						end_hour_soft,
						cost_per_hour_before_soft,
						cost_per_hour_after_soft,
					] = Object.values(item.soft_time_window);
					this.equivalentFields[init_date] = init_date_soft;
					this.equivalentFields[end_date] = end_date_soft;
					this.equivalentFields[init_hour] = init_hour_soft;
					this.equivalentFields[end_hour] = end_hour_soft;
					this.equivalentFields[cost_per_hour_before] = cost_per_hour_before_soft;
					this.equivalentFields[cost_per_hour_after] = cost_per_hour_after_soft;
				}
				this.equivalent_fields_default_selected = Object.values(this.equivalentFields);
				this.equivalentFieldsResult["time_window"] = this.time_window[0];
			} else {
				this.time_window.find((item, index) => {
					const [init_date, end_date, init_hour, end_hour, cost_per_hour_before, cost_per_hour_after] =
						Object.keys(item.soft_time_window);
					const [
						init_date_soft,
						end_date_soft,
						init_hour_soft,
						end_hour_soft,
						cost_per_hour_before_soft,
						cost_per_hour_after_soft,
					] = Object.values(item.soft_time_window);
					const valueIndex = index + 1;
					this.equivalentFields[init_date + valueIndex] = init_date_soft;
					this.equivalentFields[end_date + valueIndex] = end_date_soft;
					this.equivalentFields[init_hour + valueIndex] = init_hour_soft;
					this.equivalentFields[end_hour + valueIndex] = end_hour_soft;
					this.equivalentFields[cost_per_hour_before + valueIndex] = cost_per_hour_before_soft;
					this.equivalentFields[cost_per_hour_after + valueIndex] = cost_per_hour_after_soft;
				});
				this.equivalent_fields_default_selected = Object.values(this.equivalentFields);
				this.equivalentFieldsResult["time_window"] = this.time_window;
			}
		},
		setSelectedPromptStartDepot() {
			const [latitud, longitud] = Object.values(this.init_depot);

			// Limpiar selecciones previas de init_depot
			this.clearDepotSelections("init_depot");

			// Usar claves únicas para init_depot
			this.equivalentFields["init_depot_latitude"] = latitud;
			this.equivalentFields["init_depot_longitude"] = longitud;

			// Agregar solo las nuevas columnas sin sobrescribir
			if (latitud && !this.equivalent_fields_default_selected.includes(latitud)) {
				this.equivalent_fields_default_selected.push(latitud);
			}
			if (longitud && !this.equivalent_fields_default_selected.includes(longitud)) {
				this.equivalent_fields_default_selected.push(longitud);
			}

			this.equivalentFieldsResult["init_depot"] = this.init_depot;
		},
		setSelectedPromptEndDepot() {
			const [latitud, longitud] = Object.values(this.end_depot);

			// Limpiar selecciones previas de end_depot
			this.clearDepotSelections("end_depot");

			// Usar claves únicas para end_depot
			this.equivalentFields["end_depot_latitude"] = latitud;
			this.equivalentFields["end_depot_longitude"] = longitud;

			// Agregar solo las nuevas columnas sin sobrescribir
			if (latitud && !this.equivalent_fields_default_selected.includes(latitud)) {
				this.equivalent_fields_default_selected.push(latitud);
			}
			if (longitud && !this.equivalent_fields_default_selected.includes(longitud)) {
				this.equivalent_fields_default_selected.push(longitud);
			}

			this.equivalentFieldsResult["end_depot"] = this.end_depot;
		},
		// Helper para limpiar selecciones de un depot específico
		clearDepotSelections(depotType) {
			const prefix = depotType + "_";

			// Buscar y remover las selecciones previas de este depot
			Object.keys(this.equivalentFields).forEach((key) => {
				if (key.startsWith(prefix)) {
					const value = this.equivalentFields[key];
					if (value) {
						// Remover de la lista de seleccionadas
						const index = this.equivalent_fields_default_selected.indexOf(value);
						if (index > -1) {
							this.equivalent_fields_default_selected.splice(index, 1);
						}
					}
					// Limpiar la referencia
					delete this.equivalentFields[key];
				}
			});
		},
		// Manejador para cambios en el select de init_depot
		handleInitDepotInput(value) {
			if (!value || (Array.isArray(value) && value.length === 0)) {
				// Si se limpió el campo, remover selecciones
				this.clearDepotSelections("init_depot");
				delete this.equivalentFieldsResult["init_depot"];
			} else {
				// Si se seleccionó algo, procesar normalmente
				this.setSelectedPromptStartDepot();
			}
		},
		// Manejador para cambios en el select de end_depot
		handleEndDepotInput(value) {
			if (!value || (Array.isArray(value) && value.length === 0)) {
				// Si se limpió el campo, remover selecciones
				this.clearDepotSelections("end_depot");
				delete this.equivalentFieldsResult["end_depot"];
			} else {
				// Si se seleccionó algo, procesar normalmente
				this.setSelectedPromptEndDepot();
			}
		},
		setSelectedPromptBreaks() {
			this.breaks.find((item, index) => {
				const [init_date_window, end_date_window, init_hour_window, end_hour_window] = Object.keys(item);
				const [init_date, end_date, init_hour, end_hour] = Object.values(item);
				const valueIndex = index + 1;
				this.equivalentFields[init_date_window + valueIndex] = init_date;
				this.equivalentFields[end_date_window + valueIndex] = end_date;
				this.equivalentFields[init_hour_window + valueIndex] = init_hour;
				this.equivalentFields[end_hour_window + valueIndex] = end_hour;
			});
			this.equivalent_fields_default_selected = Object.values(this.equivalentFields);
			this.equivalentFieldsResult["breaks"] = this.breaks;
		},
		setSelectedPromptTerritories() {
			this.territories.find((item, index) => {
				const [id, priority] = Object.keys(item);
				const [id_territory, priority_territory] = Object.values(item);
				const valueIndex = index + 1;
				this.equivalentFields[id + valueIndex] = id_territory;
				this.equivalentFields[priority + valueIndex] = priority_territory;
			});
			this.equivalent_fields_default_selected = Object.values(this.equivalentFields);
			this.equivalentFieldsResult["territories"] = this.territories;
		},
		setEquivalents() {
			for (const property in this.equivalent_fields) {
				const valueProperty = this.equivalent_fields[property];
				this.processProperty(property, valueProperty);
			}
			this.equivalent_fields_default_selected = Object.values(this.equivalentFields);
		},
		processProperty(property, valueProperty) {
			if (valueProperty) {
				if (valueProperty === "add") {
					this.equivalentFields[property] = property;
				} else {
					this.handleNonAddProperty(property, valueProperty);
				}
			} else {
				// Read previous value before clearing it
				const prevVal = this.equivalentFields[property];
				this.equivalentFields[property] = "";
				delete this.equivalentFieldsResult[property];
				// Remove previous column from selected list so it becomes available again
				if (prevVal) {
					const prevEntries = Array.isArray(prevVal) ? prevVal : [prevVal];
					prevEntries.forEach((v) => {
						const idx = this.equivalent_fields_default_selected.indexOf(v);
						if (idx > -1) this.equivalent_fields_default_selected.splice(idx, 1);
					});
				}
			}
		},
		handleNonAddProperty(property, valueProperty) {
			// Remove previous value from selected list before setting the new one
			const prevVal = this.equivalentFields[property];
			if (prevVal && prevVal !== valueProperty) {
				const prevEntries = Array.isArray(prevVal) ? prevVal : [prevVal];
				prevEntries.forEach((v) => {
					const idx = this.equivalent_fields_default_selected.indexOf(v);
					if (idx > -1) this.equivalent_fields_default_selected.splice(idx, 1);
				});
			}

			if (valueProperty !== "") {
				this.equivalentFieldsResult[property] = valueProperty;
			} else {
				delete this.equivalentFieldsResult[property];
			}
			const valuePropertyFilter = [valueProperty].filter(Array.isArray);
			if (valuePropertyFilter.length > 0) {
				this.handleArrayProperty(property, valuePropertyFilter);
			} else {
				this.equivalentFields[property] = valueProperty;
			}
		},
		handleArrayProperty(property, valuePropertyFilter) {
			valuePropertyFilter.forEach((item) => {
				Object.values(item).forEach((value, index) => {
					this.equivalentFields[property + index] = value;
				});
			});
		},
		openLoading() {
			this.$vs.loading();
		},
		closeLoading() {
			this.$vs.loading.close();
		},
		enableHeadTable(headName) {
			return this.customConfig.columns.includes(headName);
		},
		addNewData() {
			this.sidebarData = {};
			this.toggleDataSidebar(true);
		},
		editData(data) {
			this.$emit("selectedAlarmEmit", data);
		},
		toggleDataSidebar(val = false) {
			this.addNewDataSidebar = val;
		},
		cahngeCurrentPageLocal() {
			this.currentPageLocal = 1;
		},
		validateUploadEquivalents() {
			if (!this.validateRequiredKeys("UPLOAD")) {
				this.alert_title = this.$t("Routing.Messages.MatchColumnSelectionRoutingErrorTitle");
				this.alert_message = `${this.$t(
					"Routing.Messages.MatchColumnSelectionRoutingError"
				)} ${this.missingRequiredKeys("UPLOAD").join(", ")}`;
				this.alert_color = "warning";
				this.popupTitle = this.$t("Routing.Buttons.Validate");
				this.popupActiveAlert = true;
				return;
			}
			const equivalentFields = {...(this.equivalentFieldsResult || {})};
			// Remove territories and strict_territories if the territories prompt was not completed
			if (!this.equivalent_array["territories"] || this.equivalent_array["territories"].length === 0) {
				delete equivalentFields["territories"];
				delete equivalentFields["strict_territories"];
			}
			let payload = {
				bucket_path: this.$store.state.uploadFile.uploadedInfoTemporal.bucket_path,
				equivalent_fields: equivalentFields,
			};
			if (this.typeUploadFile === "VISIT_POINT_UPLOAD") {
				payload.isDelivery = true;
				payload.isPickup = true;
				this.$store.commit("moduleVisitPoint/SET_UPLOAD_FILE_VISIT_POINTS_OPTIMIZATION", payload);
				this.$store.commit("moduleVisitPoint/ADD_UPLOAD_FILE_VISIT_POINT_CHECKED_PICKUP", payload);
				this.$store.commit("moduleVisitPoint/ADD_UPLOAD_FILE_VISIT_POINT_CHECKED_DELIVERY", payload);
			} else if (this.typeUploadFile === "VEHICLE_UPLOAD") {
				this.$store.commit("vehicle/SET_UPLOAD_FILE_VEHICLE", payload);
			} else if (this.typeUploadFile === "JOB_UPLOAD") {
				this.$store.commit("moduleOptimization/SET_UPLOAD_FILE_JOB", payload);
				this.submitJobUploadFile(payload);
				return;
			}
			// Habilitar el botón de siguiente al guardar
			this.$emit("disableNextButton", false);
			this.$emit("closeEquivalenceEmit");
		},
		submitJobUploadFile(payload) {
			this.isProcessing = true;
			this.openLoading();
			const apiPayload = {
				jobs: {
					file_jobs: [
						{
							bucket_path: payload.bucket_path,
							equivalent_fields: payload.equivalent_fields,
						},
					],
				},
			};
			this.$store
				.dispatch("moduleOptimization/generateStandardInputFilter", apiPayload)
				.then((res) => {
					if (res.data && res.data.jobs && res.data.jobs.length > 0) {
						this.$store.commit("moduleOptimization/SET_JOB_STANDARD_INPUT", res.data.jobs);
					}
					this.$store.commit("moduleOptimization/REMOVE_ALL_UPLOAD_FILE_JOB");
					this.$store.commit("moduleOptimization/REMOVE_ALL_CHECKED_UPLOAD_FILE_JOB");
					this.closeLoading();
					this.isProcessing = false;
					this.$vs.notify({
						color: "success",
						title: this.$t("UploadFiles.UploadFiles"),
						text: this.$t("Jobs.JobsWereSuccessfullyAdded"),
					});
					this.$emit("jobUploadCompleteEmit");
				})
				.catch((err) => {
					this.closeLoading();
					this.isProcessing = false;
					this.$vs.notify({
						color: "warning",
						title: this.$t("ErrorTitle"),
						text: this.$t("ErrorMessage"),
						time: 7000,
					});
					console.error(err);
				});
		},
		updateUploadFile() {
			if (!this.validateRequiredKeys("UPDATE")) {
				this.handleMissingKeys();
				return;
			}
			const payload = this.createPayload();
			this.openLoading();
			this.$store
				.dispatch("moduleOptimization/UpdateOptimizationFromFile", payload)
				.then(this.handleSuccess)
				.catch(this.handleError);
			// Habilitar el botón de siguiente al actualizar
			this.$emit("disableNextButton", false);
			this.$emit("closeEquivalenceEmit");
		},
		handleMissingKeys() {
			this.alert_title = this.$t("Routing.Messages.MatchColumnSelectionRoutingErrorTitle");
			this.alert_message = `${this.$t(
				"Routing.Messages.MatchColumnSelectionRoutingError"
			)} ${this.missingRequiredKeys("UPDATE").join(", ")}`;
			this.alert_color = "warning";
			this.popupTitle = this.$t("Routing.Buttons.Validate");
			this.popupActiveAlert = true;
		},
		createPayload() {
			const equivalentFields = {...(this.equivalentFieldsResult || {})};
			// Remove territories and strict_territories if the territories prompt was not completed
			if (!this.equivalent_array["territories"] || this.equivalent_array["territories"].length === 0) {
				delete equivalentFields["territories"];
				delete equivalentFields["strict_territories"];
			}
			let payload = {
				bucket_path: this.$store.state.uploadFile.uploadedInfoTemporal.bucket_path,
				equivalent_fields: equivalentFields,
			};
			if (this.typeServiceVisitPoint === "PICKUP") payload.pickups = this.standardInput["pickups"];
			if (this.typeServiceVisitPoint === "DELIVERY") payload.pickups = this.standardInput["deliveries"];
			if (this.typeServiceVisitPoint === "VEHICLE") payload.vehicles = this.standardInput["vehicles"];
			return payload;
		},
		handleSuccess(res) {
			this.closeLoading();
			if (this.typeServiceVisitPoint === "PICKUP") this.standardInput["pickups"] = res;
			if (this.typeServiceVisitPoint === "DELIVERY") this.standardInput["deliveries"] = res;
			if (this.typeServiceVisitPoint === "VEHICLE") this.standardInput["vehicles"] = res;
			this.$vs.notify({
				color: "success",
				title: this.getSuccessTitle(),
				text: this.getSuccessMessage(),
			});
			return res;
		},
		handleError(err) {
			this.closeLoading();
			this.$vs.notify({
				color: "danger",
				title: this.$t("ErrorTitle"),
				text: this.$t("ErrorMessage"),
			});
			return err;
		},
		getSuccessTitle() {
			if (this.typeServiceVisitPoint === "PICKUP") return this.$t("PickUpService");
			if (this.typeServiceVisitPoint === "DELIVERY") return this.$t("DeliveryService");
			return this.$t("Vehicles.Vehicles");
		},
		getSuccessMessage() {
			return this.typeServiceVisitPoint === "PICKUP" || this.typeServiceVisitPoint === "DELIVERY"
				? this.$t("VisitingPointsCorrectlyUpdated")
				: this.$t("VehiclesUpdatedCorrectly");
		},
		manageErrorAlert(err) {
			const findErrorNameProperty = Commons.findErrorNameProperty();
			const nameProperty = findErrorNameProperty(err);
			this.alert_title = this.$t("Routing.Messages.MatchColumnSelectionRoutingErrorTitle");
			this.alert_message = `${this.$t(
				"Routing.Messages.MatchColumnSelectionRoutingError"
			)} ${nameProperty.toUpperCase()}`;
			this.alert_color = "danger";
			this.popupTitle = this.$t("Error");
			this.popupActiveAlert = true;
		},
		manageArrayErrors(data = []) {
			this.equivalentDataErrors = data;
			this.equivalentDataErrorsAlert = true;
		},
		findText(searchStr, str) {
			return str.search(searchStr) !== -1 ? true : false;
		},
		/**
		 * Automatically assigns equivalent fields based on the keys present in the current upload.
		 * @method autoEquivalents
		 * @returns {void}
		 */
		autoEquivalents() {
			const keys = [...this.$store.state.uploadFile.uploadedInfoTemporal.keys];
			for (const key of this.required_keys) {
				if (keys.includes(key)) {
					if (
						key === "skills" ||
						key === "capacity" ||
						key === "additional_info" ||
						key === "demand" ||
						key === "allowed_vehicles"
					) {
						this.equivalent_fields[key] = [key];
					} else if (key === "time_window") {
						if (this.typeUploadFile === "VISIT_POINT_UPLOAD") {
							this.equivalent_fields[key] = [];
						} else {
							this.equivalent_fields[key] = {};
						}
					} else if (key === "breaks" || key === "territories") {
						this.equivalent_fields[key] = [];
					} else {
						this.equivalent_fields[key] = key;
					}
				}
			}
		},
		addTimeWindow() {
			if (this.typeUploadFile !== "VISIT_POINT_UPLOAD") {
				return;
			}
			const isWindowComplete =
				this.time_window[this.time_window.length - 1].init_date_window !== "" &&
				this.time_window[this.time_window.length - 1].end_date_window !== "" &&
				this.time_window[this.time_window.length - 1].init_hour_window !== "" &&
				this.time_window[this.time_window.length - 1].end_hour_window !== "";
			if (isWindowComplete) {
				this.time_window.push({
					init_date_window: "",
					end_date_window: "",
					init_hour_window: "",
					end_hour_window: "",
				});
				this.validateTimeWindow = false;
			} else {
				this.validateTimeWindow = true;
			}
		},
		removeTimeWindow(index) {
			if (this.time_window.length > 1) {
				this.time_window.splice(index, 1);
				this.validateTimeWindow = false;
			}
		},
		acceptPrompt() {
			const isWindowComplete =
				this.time_window[this.time_window.length - 1].init_date_window !== "" &&
				this.time_window[this.time_window.length - 1].end_date_window !== "" &&
				this.time_window[this.time_window.length - 1].init_hour_window !== "" &&
				this.time_window[this.time_window.length - 1].end_hour_window !== "";
			if (isWindowComplete) {
				this.optionTimeWindow = [];
				if (this.typeUploadFile === "VEHICLE_UPLOAD") {
					const firstWindow = this.time_window[0];
					this.optionTimeWindow.push({
						label: `time_window`,
						value: firstWindow,
					});
					this.equivalent_array["time_window"] = this.optionTimeWindow;
				} else {
					if (this.time_window.length > 1) {
						delete this.time_window[0].soft_time_window;
					}
					this.time_window.forEach((window, index) => {
						this.optionTimeWindow.push({
							label: `time_window # ${index + 1}`,
							value: window,
						});
					});
					this.equivalent_array["time_window"] = this.optionTimeWindow;
				}

				//La validación es exitosa, ya que se completó una ventana
				this.validateTimeWindow = false;
			} else {
				//La validación es exitosa, ya que se completó una ventana
				this.validateTimeWindow = true;
				this.activePrompt = true;
				return false;
			}
		},
		acceptPromptBreaks() {
			this.optionBreaks = [];
			for (let index in this.breaks) {
				this.optionBreaks.push({
					label: `break # ${parseInt(index) + 1}`,
					value: this.breaks[index],
				});
			}
			this.equivalent_array["breaks"] = this.optionBreaks;
		},
		addBreak() {
			if (
				this.breaks[0].init_date_window !== "" &&
				this.breaks[0].end_date_window !== "" &&
				this.breaks[0].init_hour_window !== "" &&
				this.breaks[0].end_hour_window !== ""
			) {
				this.breaks.unshift({
					init_date_window: "",
					end_date_window: "",
					init_hour_window: "",
					end_hour_window: "",
				});
				this.validateBreak = false;
			} else {
				this.validateBreak = true;
			}
		},
		removeBreak(index) {
			if (this.breaks.length > 1) this.breaks.splice(index, 1);
		},
		acceptPromptTerritories() {
			this.optionTerritories = [];
			for (let index in this.territories) {
				this.optionTerritories.push({
					label: `territory # ${parseInt(index) + 1}`,
					value: this.territories[index],
				});
			}
			this.equivalent_array["territories"] = this.optionTerritories;
		},
		addTerritory() {
			if (this.territories[0].id !== "") {
				this.territories.unshift({
					id: "",
					priority: "",
				});
				this.validateTerritory = false;
			} else {
				this.validateTerritory = true;
			}
		},
		removeTerritory(index) {
			if (this.territories.length > 1) this.territories.splice(index, 1);
		},
		acceptPromptCostPerVehicles() {
			const item = this.costPerVehicles[0];
			if (!item.identification || !item.cost) {
				this.validateCostPerVehicle = true;
				return;
			}
			this.validateCostPerVehicle = false;
			this.optionCostPerVehicles = [
				{
					label: this.$t("Jobs.CostPerVehicle"),
					value: item,
				},
			];
			this.equivalent_array["cost_per_vehicles"] = this.optionCostPerVehicles;
			this.setSelectedPromptCostPerVehicles();
		},
		setSelectedPromptCostPerVehicles() {
			const item = this.costPerVehicles[0];
			if (item.identification) {
				this.equivalentFields["cost_per_vehicle_identification"] = item.identification;
			}
			if (item.cost) {
				this.equivalentFields["cost_per_vehicle_cost"] = item.cost;
			}
			this.equivalent_fields_default_selected = Object.values(this.equivalentFields);
			this.equivalentFieldsResult["cost_per_vehicles"] = [item];
		},
		validateRequiredKeys(typeAction) {
			const keys1 = Object.keys(typeAction === "UPDATE" ? {identification: ""} : this.validateJsonBase);
			const keys2 = Object.keys(this.equivalent_fields);
			const haveSameProperties = keys1.every((key) => {
				if (!keys2.includes(key)) return false;
				const val = this.equivalent_fields[key];
				if (val == null || val === "") return false;
				if (Array.isArray(val) && val.length === 0) return false;
				return true;
			});
			return haveSameProperties;
		},
		missingRequiredKeys(typeAction) {
			const keys1 = Object.keys(typeAction === "UPDATE" ? {identification: ""} : this.validateJsonBase);
			const keys2 = Object.keys(this.equivalent_fields);
			return keys1.filter((key) => {
				if (!keys2.includes(key)) return true;
				const val = this.equivalent_fields[key];
				if (val == null || val === "") return true;
				if (Array.isArray(val) && val.length === 0) return true;
				return false;
			});
		},
		acceptPromptStartDeposit() {
			this.optionStartDeposit = [];
			this.optionStartDeposit.push({
				label: `init_depot`,
				value: this.init_depot,
			});
			this.equivalent_array["init_depot"] = this.optionStartDeposit;
		},
		acceptPromptEndDeposit() {
			this.optionEndDeposit = [];
			this.optionEndDeposit.push({
				label: `end_depot`,
				value: this.end_depot,
			});
			this.equivalent_array["end_depot"] = this.optionEndDeposit;
		},
		isFieldRequired(key) {
			const requiredFields = Object.keys(this.validateJsonBase);
			return requiredFields.includes(key);
		},
		isFieldMapped(key) {
			return (
				this.equivalent_fields[key] &&
				this.equivalent_fields[key] !== "" &&
				this.equivalent_fields[key] !== null
			);
		},
		getFieldCardClass(key) {
			const baseClass = "p-4 border rounded-lg";
			if (this.isFieldMapped(key)) {
				return `${baseClass} border-green-200 bg-green-50/50`;
			} else if (this.isFieldRequired(key)) {
				return `${baseClass} border-red-200 bg-red-50/50`;
			}
			return `${baseClass} border-gray-200 bg-gray-50/30`;
		},
		getFieldIconClass(key) {
			if (this.isFieldMapped(key)) {
				return "text-primary";
			} else if (this.isFieldRequired(key)) {
				return "bg-red-100 text-red-600";
			}
			return "bg-gray-100 text-gray-600";
		},
		getFieldIcon(key) {
			const iconMap = {
				identification: "TagIcon",
				longitude: "MapPinIcon",
				latitude: "MapPinIcon",
				priority: "StarIcon",
				time_service: "ClockIcon",
				skills: "ToolIcon",
				demand: "TrendingUpIcon",
				allowed_vehicles: "TruckIcon",
				time_window: "CalendarIcon",
				additional_info: "InfoIcon",
				territory_id: "MapIcon",
				profile: "UserIcon",
				max_travel_distance: "CornerRightUpIcon",
				max_travel_duration: "ClockIcon",
				init_depot: "HomeIcon",
				end_depot: "FlagIcon",
				capacity: "PackageIcon",
				breaks: "PauseIcon",
				time_cost: "DollarSignIcon",
				traveled_hour_cost: "DollarSignIcon",
				distance_cost: "DollarSignIcon",
				fixed_cost: "DollarSignIcon",
				strict_territories: "LockIcon",
				territories: "MapIcon",
				original_identification: "LinkIcon",
				name: "TagIcon",
				pickups: "TruckIcon",
				deliveries: "PackageIcon",
				cost_per_vehicles: "DollarSignIcon",
				ignore: "EyeOffIcon",
				penalty_cost: "AlertCircleIcon",
				pickup_to_delivery_absolute_detour_limit: "ClockIcon",
				pickup_to_delivery_time_limit: "ClockIcon",
				pickup_to_delivery_relative_detour_limit: "TrendingUpIcon",
			};
			return iconMap[key] || "CircleIcon";
		},
		getFieldDescription(key) {
			return this.fieldDescriptions[key] || "";
		},
		getFieldHint(key) {
			return this.fieldHints[key] || "";
		},
		getFieldPlaceholder(key) {
			if (this.isSelectMultiple(key)) {
				return this.$t("FieldHints.select_multiple", {key: this.resolvePoliticalDivisionName(key)});
			}
			return this.$t("FieldHints.select_single", {key: this.resolvePoliticalDivisionName(key)});
		},
		onFieldChange(key, value) {
			this.setSelected();
			this.updateValidationErrors();

			// Forzar actualización de las sugerencias después de mapear un campo
			this.$nextTick(() => {
				this.$forceUpdate(); // Esto hará que se recalculen las sugerencias
			});
		},
		updateValidationErrors() {
			this.validationErrors = this.missingRequiredKeys("UPLOAD");
		},
		getSmartSuggestions(key) {
			const suggestions = [];
			const headers = this.headers;

			// Obtener todas las columnas que ya están siendo usadas en otros campos
			const usedColumns = new Set();
			Object.keys(this.equivalent_fields).forEach((fieldKey) => {
				if (fieldKey !== key && this.equivalent_fields[fieldKey]) {
					// Si es array (campos múltiples), agregar todos los elementos
					if (Array.isArray(this.equivalent_fields[fieldKey])) {
						this.equivalent_fields[fieldKey].forEach((col) => {
							if (col) usedColumns.add(col);
						});
					} else {
						// Si es string simple, agregarlo directamente
						usedColumns.add(this.equivalent_fields[fieldKey]);
					}
				}
			});

			// Smart matching based on field name similarity
			const fieldPatterns = {
				identification: ["id", "identifier", "codigo", "code"],
				longitude: ["lng", "lon", "longitud", "x"],
				latitude: ["lat", "latitud", "y"],
				priority: ["prioridad", "prio", "nivel"],
				time_service: ["tiempo", "time", "duracion", "duration"],
				skills: ["habilidad", "skill", "competencia"],
				demand: ["demanda", "cantidad", "volumen", "peso"],
			};

			if (fieldPatterns[key]) {
				headers.forEach((header) => {
					const headerLower = header.toLowerCase();
					fieldPatterns[key].forEach((pattern) => {
						// Solo agregar si la columna no está ya siendo usada en otro campo
						// Y no está ya en las sugerencias
						if (
							headerLower.includes(pattern) &&
							!suggestions.includes(header) &&
							!usedColumns.has(header)
						) {
							suggestions.push(header);
						}
					});
				});
			}

			return suggestions.slice(0, 3); // Maximum 3 suggestions
		},
		debugUsedColumns() {
			const usedColumns = new Set();
			const mappedFields = {};

			Object.keys(this.equivalent_fields).forEach((fieldKey) => {
				if (this.equivalent_fields[fieldKey]) {
					mappedFields[fieldKey] = this.equivalent_fields[fieldKey];

					if (Array.isArray(this.equivalent_fields[fieldKey])) {
						this.equivalent_fields[fieldKey].forEach((col) => {
							if (col) usedColumns.add(col);
						});
					} else {
						usedColumns.add(this.equivalent_fields[fieldKey]);
					}
				}
			});
		},
		getSuggestionChipClass(suggestion, key) {
			const isSelected = this.equivalent_fields[key] === suggestion;
			const baseClass = "group relative";

			if (isSelected) {
				return `${baseClass} bg-gradient-to-r from-emerald-500 to-emerald-600 text-white border-emerald-600 shadow-md line-through opacity-75 cursor-not-allowed`;
			}
			return `${baseClass} bg-gradient-to-r from-blue-50 to-indigo-50 text-blue-700 border border-blue-200 hover:from-blue-100 hover:to-indigo-100 hover:border-blue-300 hover:shadow-md`;
		},
		applySuggestion(key, suggestion) {
			if (this.isSelectMultiple(key)) {
				if (Array.isArray(this.equivalent_fields[key]) && this.equivalent_fields[key].length > 0) {
					this.equivalent_fields[key].push(suggestion);
				} else {
					this.equivalent_fields[key] = [suggestion];
				}
			} else {
				this.equivalent_fields[key] = suggestion;
			}
			this.onFieldChange(key, this.equivalent_fields[key]);
			setTimeout(() => {
				this.debugUsedColumns();
			}, 100);
		},
		getHeaderClass(header) {
			return this.isHeaderMapped(header) ? "mapped-header" : "";
		},
		isHeaderMapped(header) {
			return Object.values(this.equivalent_fields).includes(header);
		},
		getCellClass(header, value) {
			const baseClass = "relative";
			if (this.isCellEmpty(value)) {
				return `${baseClass} empty-cell`;
			}
			if (this.isHeaderMapped(header)) {
				return `${baseClass} mapped-cell`;
			}
			return baseClass;
		},
		formatCellValue(value) {
			if (value === null || value === undefined || value === "") {
				return "—";
			}
			if (typeof value === "string" && value.length > 20) {
				return value.substring(0, 20) + "...";
			}
			return value;
		},
		isCellEmpty(value) {
			return value === null || value === undefined || value === "";
		},
		handleCancel() {
			// Habilitar el botón de siguiente al cancelar
			this.$emit("disableNextButton", false);
			this.$emit("cancelEquivalenceEmit");
		},
		setupIntelligentDropdowns() {
			// Use MutationObserver to watch for dynamically added v-select elements
			const observer = new MutationObserver((mutations) => {
				mutations.forEach((mutation) => {
					mutation.addedNodes.forEach((node) => {
						if (node.nodeType === 1) {
							// Element node
							this.attachDropdownListeners(node);
						}
					});
				});
			});
			observer.observe(this.$el, {
				childList: true,
				subtree: true,
			});
			// Setup initial dropdowns
			this.$nextTick(() => {
				this.attachDropdownListeners(this.$el);
			});
			// Store observer for cleanup
			this.dropdownObserver = observer;
		},
		attachDropdownListeners(container) {
			const vSelects = container.querySelectorAll
				? container.querySelectorAll(".v-select")
				: container.classList && container.classList.contains("v-select")
				? [container]
				: [];

			vSelects.forEach((select) => {
				// Avoid duplicate listeners
				if (select.dataset.dropdownSetup) return;
				select.dataset.dropdownSetup = "true";

				const dropdown = select.querySelector(".vs__dropdown-toggle");
				if (dropdown) {
					const handleClick = () => {
						// Use a small delay to ensure dropdown is rendered
						setTimeout(() => {
							this.adjustDropdownPosition(select);
						}, 10);
					};

					dropdown.addEventListener("click", handleClick);

					// Also handle focus events for keyboard navigation
					dropdown.addEventListener("focus", handleClick);
				}
			});
		},
		adjustDropdownPosition(selectElement) {
			if (!selectElement) return;
			const selectRect = selectElement.getBoundingClientRect();
			const viewportHeight = window.innerHeight;
			const dropdownHeight = 350; // max-height from CSS
			const spaceBelow = viewportHeight - selectRect.bottom;
			const spaceAbove = selectRect.top;
			const scrollBuffer = 20; // Extra buffer for scroll
			// Remove existing positioning classes
			selectElement.classList.remove("vs--above", "vs--below");
			// Determine if dropdown should open upward or downward
			if (spaceBelow < dropdownHeight + scrollBuffer && spaceAbove > spaceBelow) {
				selectElement.classList.add("vs--above");
			} else {
				selectElement.classList.add("vs--below");
			}
		},
	},
	filters: {
		mayus(val = "") {
			return val.toUpperCase();
		},
	},
	watch: {
		itemsPerPage() {
			this.currentPageLocal = 1;
		},
	},
	created() {
		if (!moduleOptimization.isRegistered) {
			this.$store.registerModule("moduleOptimization", moduleOptimization);
			moduleOptimization.isRegistered = true;
		}
		this.equivalent_fields = {
			...this.$store.state.uploadFile.uploadedInfoTemporal.keys,
		};
		for (const key in this.$store.state.uploadFile.uploadedInfoTemporal.keys) {
			this.equivalent_fields[key] = "";
		}
		this.autoEquivalents();
		this.setEquivalents();
	},
	mounted() {
		this.isMounted = true;
		this.setupIntersectionObserver();
		this.setupIntelligentDropdowns();
		// Deshabilitar el botón de siguiente cuando se monta el componente
		this.$emit("disableNextButton", true);
	},
	beforeDestroy() {
		if (this.intersectionObserver) {
			this.intersectionObserver.disconnect();
		}
		if (this.dropdownObserver) {
			this.dropdownObserver.disconnect();
		}
		// Habilitar el botón de siguiente cuando se destruye el componente
		this.$emit("disableNextButton", false);
	},
};
</script>

<style lang="scss" scoped>
// Enhanced styles for modern UX/UI
.equivalences-container {
	@apply max-w-full mx-auto;
	font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
}

// Primary color utilities (using VS primary variables)
.text-primary {
	color: rgba(var(--vs-primary), 1) !important;
}

.text-primary-dark {
	color: rgba(var(--vs-primary), 0.8) !important;
}

.border-primary {
	border-color: rgba(var(--vs-primary), 1) !important;
}
// Minimal Glass Container Effect
.glass-container {
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(8px);
	border: 1px solid rgba(226, 232, 240, 0.8);
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.04), 0 1px 2px rgba(0, 0, 0, 0.02);
}

// Section Headers
.section-header {
	h3 {
		font-weight: 600;
		letter-spacing: -0.025em;
	}

	.icon-wrapper {
		border-radius: 0.75rem;
		box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
	}
}

// Minimal Field Mapping Cards
.field-mapping-card {
	background: rgba(255, 255, 255, 0.9);
	border-radius: 0.5rem;
	border: 1px solid #f1f5f9;
	box-shadow: 0 1px 3px rgba(0, 0, 0, 0.02);
	transition: all 0.2s ease;

	&:hover {
		box-shadow: 0 2px 6px rgba(0, 0, 0, 0.04);
		border-color: #e2e8f0;
	}

	.field-icon {
		border-radius: 0.5rem;
		transition: all 0.2s ease;
	}

	.field-label {
		font-weight: 500;
		color: #374151;
	}
}

// Global v-select dropdown styles with intelligent positioning
::v-deep .vs__dropdown-menu {
	z-index: 9999 !important;
	display: block;
	-webkit-box-sizing: border-box;
	box-sizing: border-box;
	position: absolute;
	width: 100%;
	max-height: 350px;
	min-width: 160px;
	overflow-y: auto;
	-webkit-box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.15);
	box-shadow: 0px 3px 6px 0px rgba(0, 0, 0, 0.15);
	list-style: none;
	background: rgba(255, 255, 255, 0.95);
	backdrop-filter: blur(20px);
	border: 1px solid rgba(255, 255, 255, 0.2);
	border-radius: 0.75rem;
}

// Dropdown opens upward when there's no space below
::v-deep .vs--open.vs--below .vs__dropdown-menu {
	top: calc(100% - 1px);
	bottom: auto;
	margin-top: 4px;
	margin-bottom: 0;
	transform-origin: top center;
	animation: dropdownSlideDown 0.15s ease-out;
}

::v-deep .vs--open.vs--above .vs__dropdown-menu {
	bottom: calc(100% - 1px);
	top: auto;
	margin-top: 0;
	margin-bottom: 4px;
	transform-origin: bottom center;
	animation: dropdownSlideUp 0.15s ease-out;
}

// Smooth animations for dropdown appearance
@keyframes dropdownSlideDown {
	from {
		opacity: 0;
		transform: translateY(-8px) scaleY(0.95);
	}
	to {
		opacity: 1;
		transform: translateY(0) scaleY(1);
	}
}

@keyframes dropdownSlideUp {
	from {
		opacity: 0;
		transform: translateY(8px) scaleY(0.95);
	}
	to {
		opacity: 1;
		transform: translateY(0) scaleY(1);
	}
}

// Enhanced Select Styles
.enhanced-select-wrapper {
	@apply relative flex items-end gap-2;

	.enhanced-select {
		@apply flex-1;

		::v-deep .vs__dropdown-toggle {
			background: rgba(255, 255, 255, 0.9);
			border: 1.5px solid #e5e7eb;
			border-radius: 0.75rem;
			min-height: 48px;
			transition: all 0.2s ease;

			&:hover {
				border-color: #6366f1;
				box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
			}
		}

		::v-deep .vs__option {
			padding: 12px 16px;
			transition: all 0.15s ease;
			color: #374151;

			&--highlight {
				background: #f1f5f9;
				color: #1e293b;
			}
		}
	}
}

// Minimal Smart Suggestions
.smart-suggestions {
	.suggestions-header {
		color: #505050;
		font-style: italic;
	}
	.suggestion-chip {
		transition: all 0.2s ease;
		font-weight: 700;
		background: rgba(var(--vs-primary), 1);
		color: #f1f5f9;
		border: 1px solid #e2e8f0;
		&:hover {
			background-color: rgba(var(--vs-primary), 0.7);
			border-color: #cbd5e1;
			box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
		}
	}
}

// No Options Container
.no-options-container {
	padding: 1.5rem;
	text-align: center;

	.feather-icon {
		opacity: 0.6;
	}
}

// Enhanced Data Table
.enhanced-data-table {
	border-collapse: separate;
	border-spacing: 0;
	background: rgba(255, 255, 255, 0.9);
	border-radius: 0.75rem;
	overflow: hidden;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);

	.table-header {
		background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
		border-bottom: 2px solid #e2e8f0;

		.header-cell {
			padding: 1rem;
			text-align: left;
			font-weight: 600;
			color: #374151;
			font-size: 0.875rem;
			letter-spacing: 0.025em;
			position: relative;

			&.mapped-header {
				background: linear-gradient(135deg, #ecfdf5 0%, #ccc 100%);
				color: rgba(var(--vs-primary), 1);
				border-bottom: 2px solid #888484;
			}

			.header-content {
				@apply flex items-center justify-between;

				.mapping-indicator {
					@apply ml-2;
				}
			}
		}
	}

	.table-row {
		transition: all 0.15s ease;

		&:hover {
			background-color: rgba(99, 102, 241, 0.05);
		}

		&:nth-child(even) {
			background-color: rgba(248, 250, 252, 0.8);
		}

		&.is-placeholder {
			&:hover,
			&:nth-child(even) {
				background-color: transparent;
			}
		}

		.table-cell {
			padding: 0.875rem 1rem;
			border-bottom: 1px solid rgba(229, 231, 235, 0.6);
			font-size: 0.875rem;

			&.mapped-cell {
				background-color: rgba(16, 185, 129, 0.05);
				border-left: 3px solid #888484;
			}

			&.empty-cell {
				background-color: rgba(245, 158, 11, 0.05);

				.cell-text {
					color: #9ca3af;
					font-style: italic;
				}
			}

			.cell-content {
				@apply flex items-center justify-between;

				.cell-text {
					@apply truncate;
					max-width: 200px;
				}

				.empty-indicator {
					@apply ml-2 flex-shrink-0;
				}
			}
		}
	}
}

// Minimal Action Buttons
.action-buttons-container {
	.cancel-btn,
	.save-btn {
		border-radius: 0.5rem !important;
		padding: 0.75rem 1.5rem !important;
		font-weight: 500 !important;
		letter-spacing: 0.025em;
		transition: all 0.2s ease !important;
	}

	.cancel-btn {
		background: #f8fafc !important;
		color: #475569 !important;
		border: 1px solid #e2e8f0 !important;

		&:hover {
			background: #f1f5f9 !important;
			border-color: #cbd5e1 !important;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
		}
	}

	.save-btn {
		background: #1e293b !important;
		color: white !important;
		border: 1px solid #1e293b !important;

		&:hover {
			background: #0f172a !important;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		}
	}
}

// Minimal Validation Summary
.validation-summary {
	border-radius: 0.5rem;
	backdrop-filter: blur(4px);
	animation: slideIn 0.3s ease-out;
	background: #fef2f2;
	border: 1px solid #fecaca;
}

@keyframes slideIn {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}

// Scrollbar Styles
.mapping-scroll-area,
.enhanced-table-scroll {
	border-radius: 0.5rem;
	height: 600px; /* Added height for scrollbar */

	::v-deep .ps__rail-y {
		background-color: rgba(0, 0, 0, 0.05);
		border-radius: 0.25rem;

		.ps__thumb-y {
			background-color: rgba(99, 102, 241, 0.3);
			border-radius: 0.25rem;

			&:hover {
				background-color: rgba(99, 102, 241, 0.5);
			}
		}
	}
}

// Preview Stats
.preview-stats {
	.stat-item {
		@apply flex flex-col items-center gap-1;

		span:first-child {
			font-weight: 400;
		}

		span:last-child {
			font-weight: 600;
		}
	}
}

// Responsive Design
@media (max-width: 768px) {
	.equivalences-container {
		padding: 1rem;
	}

	.glass-container {
		padding: 1rem;

		.grid {
			grid-template-columns: 1fr;
			gap: 2rem;
		}
	}

	.field-mapping-card {
		padding: 1rem;
	}

	.preview-stats {
		flex-direction: column;
		gap: 1rem;
		margin-left: 0 !important;
		margin-top: 1rem;
	}

	.enhanced-data-table {
		font-size: 0.75rem;

		.header-cell,
		.table-cell {
			padding: 0.5rem;
		}
	}

	.action-buttons-container {
		.flex {
			flex-direction: column;
		}
	}
}

// High contrast mode support
@media (prefers-contrast: high) {
	.glass-container {
		background: white;
		border: 2px solid #000;
	}

	.field-mapping-card {
		background: white;
		border: 1px solid #000;
	}
}

// Reduced motion support
@media (prefers-reduced-motion: reduce) {
	* {
		animation-duration: 0.01ms !important;
		animation-iteration-count: 1 !important;
		transition-duration: 0.01ms !important;
	}
}

// Dark mode support (if enabled in future)
@media (prefers-color-scheme: dark) {
	.glass-container {
		border-color: rgba(75, 85, 99, 0.3);
	}

	.field-mapping-card {
		border-color: rgba(75, 85, 99, 0.3);
	}
}

// Legacy browser fallbacks
th {
	border: 1px solid #dddddd;
	text-align: left;
	padding: 8px;
}

td {
	border-bottom: 1px solid #dddddd;
	text-align: left;
	padding: 8px;
}

.scroll-area {
	margin: auto;
	width: 100%;
	height: 400px;
}

.scroll-area-table {
	margin: auto;
	width: 100%;
	height: 450px;
}

@keyframes shimmer {
	100% {
		transform: translateX(100%);
	}
}

.skeleton-placeholder {
	width: 100%;
	height: 1rem;
	border-radius: 0.25rem;
	background-color: #e5e7eb;
	position: relative;
	overflow: hidden;

	&::after {
		content: "";
		position: absolute;
		top: 0;
		left: 0;
		right: 0;
		bottom: 0;
		transform: translateX(-100%);
		background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.6), transparent);
		animation: shimmer 1.5s infinite;
	}
}

.mapping-scroll-area {
	height: 600px; /* Added height for scrollbar */
}

.floating-actions-wrapper {
	position: fixed;
	bottom: 0;
	left: 0;
	right: 0;
	z-index: 100;
	padding: 1rem;
	pointer-events: none; /* Allows scrolling through the wrapper */

	.glass-container {
		max-width: 1200px; /* Adjust to your main container max-width */
		margin: 0 auto;
		pointer-events: auto; /* Re-enables pointer events for the content */
		box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.08);
	}
}

.sticky-actions-wrapper {
	position: sticky;
	bottom: 0;
	z-index: 50;

	/* To counteract the parent's padding and make it flush */
	margin: 1.5rem -1.5rem -1.5rem -1.5rem;
	padding: 1rem 1.5rem;

	background: rgba(255, 255, 255, 0.85);
	backdrop-filter: blur(10px);

	border-top: 1px solid rgba(226, 232, 240, 1);
	box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.05);

	/* To match the parent's rounded corners at the bottom */
	border-bottom-left-radius: 0.75rem; /* rounded-xl is 0.75rem */
	border-bottom-right-radius: 0.75rem;
}

.header-actions-wrapper {
	backdrop-filter: blur(8px);
	border: 1px solid rgba(226, 232, 240, 0.8);
	border-radius: 0.5rem;
	padding: 0.75rem 1rem;
	transition: all 0.3s ease;

	/* Animation for smooth appearance */
	animation: slideInFromTop 0.3s ease-out;
}

@keyframes slideInFromTop {
	from {
		opacity: 0;
		transform: translateY(-10px);
	}
	to {
		opacity: 1;
		transform: translateY(0);
	}
}
</style>
